import { IRequestContext, ITelemetry } from '@msdyn365-commerce/core';
import { useEffect } from 'react';

import { getCookie } from '../../../shared/functions/cookie';
import { CookieNames } from '../../../shared/services/cookie-service/cookie-service.const';
import { isUserAuthenticated } from '../../../utils/is-user-authenticated';

export interface IUseLocaleRedirectOptions {
    request: IRequestContext;
    telemetry: ITelemetry;
    shouldRedirectToLocale?: boolean;
    deleteCookiesOnLocaleSwitch?: boolean;
    shouldLogOutInvalidLocaleUser?: boolean;
    cookieExpiration: number | undefined;
}

const DEV_PROXY_SEGMENT = 'dev';

const deriveLocalePath = (pathname: string): string => {
    const segments = pathname.split('/').filter(segment => segment !== '');
    const [firstSegment] = segments[0] === DEV_PROXY_SEGMENT ? segments.slice(1) : segments;
    return firstSegment ? `/${firstSegment}` : '/';
};

const deleteAllCookies = (): void => {
    document.cookie.split(';').forEach(cookie => {
        const [name] = cookie.split('=');
        document.cookie = `${name.trim()}=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
    });
};

export const useLocaleRedirect = (options: IUseLocaleRedirectOptions): void => {
    const {
        request,
        telemetry,
        shouldRedirectToLocale = false,
        deleteCookiesOnLocaleSwitch = false,
        shouldLogOutInvalidLocaleUser = false
    } = options;

    const rawCookieExpiration = options.cookieExpiration;

    const isAuthenticated = isUserAuthenticated(request);
    const signOutUrl = request.user?.signOutUrl;

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        if (shouldRedirectToLocale) {
            if (window.location.pathname !== '/') {
                return;
            }
            const storedLocalePath = getCookie(CookieNames.Locale);
            if (storedLocalePath !== '') {
                window.location.replace(storedLocalePath);
            }
            return;
        }

        const previousLocalePath = getCookie(CookieNames.Locale);
        const currentLocalePath = deriveLocalePath(window.location.pathname);

        const cookieExpirationFromConfig = Number(rawCookieExpiration);
        const cookieExpiration = isNaN(cookieExpirationFromConfig) ? 0 : cookieExpirationFromConfig;

        const expires = new Date();
        expires.setDate(expires.getDate() + cookieExpiration);
        // Single overwrite at path=/; the previous clearCookie targeted the page path and was a no-op.
        document.cookie = `${CookieNames.Locale}=${currentLocalePath}; expires=${expires.toUTCString()}; path=/;`;

        const hasSwitchedLocale = previousLocalePath !== '' && previousLocalePath !== currentLocalePath;
        if (!hasSwitchedLocale) {
            return;
        }

        if (deleteCookiesOnLocaleSwitch) {
            // Legacy parity: runs after the locale cookie write, so the fresh locale cookie is wiped too.
            deleteAllCookies();
        }

        if (shouldLogOutInvalidLocaleUser && isAuthenticated) {
            telemetry.warning('User logged in on invalid locale. Logging out user.');
            window.location.href = signOutUrl ?? '';
        }
    }, [
        shouldRedirectToLocale,
        deleteCookiesOnLocaleSwitch,
        shouldLogOutInvalidLocaleUser,
        rawCookieExpiration,
        isAuthenticated,
        signOutUrl,
        telemetry
    ]);
};
